import type { Decimal } from '../math/num';
import { D, add, mul, div } from '../math/num';
import type { MindGraph } from './types';
import { topoOrder } from './topo';
import { getPartDef, upgradeScale, levelGrowth } from '../data/parts/partDefs';
import { isMechCore, coreTierMult, coreLevelMult, portsForNode } from '../data/mechCore';
import {
  emptyMods,
  emptyGlobal,
  RARITY_MULT,
  type ModBundle,
  type GlobalMods,
  type EffectiveAttack,
  type PartInstance,
  type AffixStat,
} from '../data/parts/partTypes';
import type { PartDef } from '../data/parts/partDefs';

/** Rarity multiplies a part's primary effect. The intrinsic Mech Core is exempt
 *  (its growth comes from upgrades and Ascension taps, not loot luck). */
function rarityMultOf(def: PartDef, inst: PartInstance | undefined): number {
  if (isMechCore(def.id)) return 1;
  return RARITY_MULT[inst?.rarity ?? def.baseRarity];
}

/**
 * Evaluator v2 (THE_MIND_AND_PARTS.md §5). Two passes over a wired graph of part
 * instances → effective attacks + global modifiers. Pure & deterministic (no RNG,
 * no clock). Same-stat tuners stack additively; amplifiers multiply power.
 */

export const MIN_COOLDOWN_MS = 100;

/** Total cooldown reduction is HARD-CAPPED: haste is a garnish, not a build.
 *  (Stacked haste tuners + affixes clamp here; protects sim and renderer.) */
export const CD_RED_CAP = 0.2;

/**
 * Booster stacking penalty (replaces P37's intake saturation). Flow carries a
 * BOOST COUNT: how many boosters (amplifiers) it has already passed through.
 * A booster receiving count k works at chain position k+1; each position
 * keeps this fraction of the previous position's bonus effectiveness
 * (position 1 = 100%, then ×BOOST_DECAY per step). The penalty hits the
 * BONUS only (a dead booster degrades to a pass-through, never a drain), so
 * more is always more. Raw tap fan-in at attacks is full value again: the
 * supercharge promise holds, the geometric amp chain is what pays.
 */
export const BOOST_DECAY = 0.5;

/** A booster's bonus effectiveness at chain position pos (1-based).
 *  `conductance` is the fraction of the base penalty that REMAINS: 1 with no
 *  Grid ranks, falling toward 0 (never reaching it) as Conductance stacks. */
export function boostEffectiveness(pos: number, conductance = 1): number {
  const base = Math.pow(BOOST_DECAY, Math.max(0, pos - 1));
  return 1 - (1 - base) * conductance;
}

/** A packet of Power: its value, the count of boosters it has traversed, and
 *  what it would be worth had no stacking penalty applied (for the UI). */
interface FlowBucket {
  v: Decimal;
  full: Decimal;
}
type Buckets = Map<number, FlowBucket>; // boost count -> packet

/** Per-attack damage breakdown (the stair-step), for in-node readouts/tooltips. */
export interface AttackBreakdown {
  base: Decimal; // baseDamage × level scale (no flow)
  flowIn: Decimal; // Power arriving at the attack's flow input (penalties already applied upstream)
  flowScale: Decimal; // how much each unit of flow adds
  flowContribution: Decimal; // flowIn × flowScale
  powerAddFrac: Decimal; // Σ additive +power% (affixes + power tuners)
  powerMult: Decimal; // Π multiplicative ×power (amplifier-style mods)
  critChanceAdd: number;
  critMultAdd: Decimal;
  cooldownRed: number;
}

/** Per-booster stacking readout, keyed by node id. */
export interface BoosterInfo {
  /** Power-weighted bonus effectiveness applied by this booster (0..1). */
  eff: number;
  /** Deepest chain position this booster occupies (1 = first in line). */
  topPos: number;
}

export interface GraphEvaluationV2 {
  attacks: EffectiveAttack[];
  global: GlobalMods;
  /** Power arriving at each node's flow inputs — for wire-flow visualization. */
  powerInByNode: Record<string, Decimal>;
  /** Power a node emits per output tap BEFORE the splitting divisor — for readouts. */
  powerOutByNode: Record<string, Decimal>;
  /** Power carried by each flow edge (by edge id), AFTER the splitting divisor. */
  edgeFlow: Record<string, Decimal>;
  /** Fraction of an edge's carried power surviving stacking penalties so far
   *  (1 = clean). Drives the green/yellow/red wire tint. */
  edgeEff: Record<string, number>;
  /** Stacking readout per booster node. */
  boosterInfo: Record<string, BoosterInfo>;
  /** Per-attack stair-step breakdown, keyed by source node id. */
  attackInfo: Record<string, AttackBreakdown>;
  equippedWeight: Decimal;
}

function affixSum(inst: PartInstance | undefined, stat: AffixStat): Decimal {
  if (!inst) return D(0);
  let total = D(0);
  for (const a of inst.affixes) if (a.stat === stat) total = add(total, a.value);
  return total;
}

function mergeMod(into: ModBundle, from: ModBundle): void {
  into.powerAdd = add(into.powerAdd, from.powerAdd);
  into.powerMult = mul(into.powerMult, from.powerMult);
  into.critChanceAdd += from.critChanceAdd;
  into.critMultAdd = add(into.critMultAdd, from.critMultAdd);
  into.cooldownRed += from.cooldownRed;
}

function clamp01(n: number): number {
  return Math.max(0, Math.min(1, n));
}

/**
 * @param coreBoost multiplier on the Mech Core's power output (Star Harvest's
 *   Dyson swarm, up to ×2).
 * @param conductance fraction of the booster stacking penalty that remains
 *   (1 = no Grid Conductance; falls toward 0, never reaching it).
 */
export function evaluateGraphV2(graph: MindGraph, instances: PartInstance[], tier = 1, coreBoost = 1, conductance = 1): GraphEvaluationV2 {
  const instById = new Map(instances.map((i) => [i.instanceId, i]));
  const instOf = (nodeId: string): PartInstance | undefined => {
    const node = graph.nodes.find((n) => n.id === nodeId);
    return node?.instanceId ? instById.get(node.instanceId) : undefined;
  };

  // ---- weight + global mods (scan all equipped parts) ----
  const global: GlobalMods = emptyGlobal();
  let equippedWeight = D(0);
  for (const node of graph.nodes) {
    const def = getPartDef(node.defId);
    const inst = instOf(node.id);
    const weightRed = affixSum(inst, 'weightRed');
    equippedWeight = add(equippedWeight, D(def.weight).sub(weightRed).max(0));
    if (def.utility) {
      // perLevel growth is diminishing ((L-1)^0.7); it is the ONLY level scaling
      // here (a former double-scale with upgradeScale made these quadratic).
      const amt = (def.utility.base + def.utility.perLevel * levelGrowth(node.level)) * rarityMultOf(def, inst);
      const mAmt = D(amt);
      if (def.utility.stat === 'dropRate') global.dropRateMult = add(global.dropRateMult, mAmt);
      if (def.utility.stat === 'scrapMult') global.scrapMult = add(global.scrapMult, mAmt);
      if (def.utility.stat === 'componentMult') global.componentMult = add(global.componentMult, mAmt);
      if (def.utility.stat === 'regen') global.regenAdd += mAmt.toNumber();
    }
    // global affixes apply wherever they roll
    global.dropRateMult = add(global.dropRateMult, affixSum(inst, 'dropRate'));
    global.scrapMult = add(global.scrapMult, affixSum(inst, 'scrapMult'));
    global.componentMult = add(global.componentMult, affixSum(inst, 'componentMult'));
  }

  // ---- PHASE 1: flow ----
  // Power moves as BUCKETS keyed by boost count, so every packet knows how
  // many boosters it has traversed. Merging at an input is a per-count union:
  // clean power is never penalized for sharing a wire with boosted power.
  const powerOut = new Map<string, Decimal>(); // node -> total power emitted (pre-split)
  const bucketsIn = new Map<string, Buckets>(); // node -> packets at flow inputs
  const modIn = new Map<string, ModBundle>(); // node -> merged mods at modifier inputs
  for (const n of graph.nodes) {
    bucketsIn.set(n.id, new Map());
    modIn.set(n.id, emptyMods());
  }

  const edgeFlow: Record<string, Decimal> = {};
  const edgeEff: Record<string, number> = {};
  const boosterInfo: Record<string, BoosterInfo> = {};
  const order = topoOrder(graph);
  for (const node of order) {
    const def = getPartDef(node.defId);
    const inst = instOf(node.id);

    // Tuners emit a mod bundle into every modifier port they're wired to (no split).
    if (def.tuner) {
      const tb = tunerBundle(def, node.level, inst);
      for (const e of graph.edges) {
        if (e.fromNode !== node.id) continue;
        const target = modIn.get(e.toNode);
        if (target) mergeMod(target, tb);
      }
      continue;
    }

    // Power-emitting nodes: sources push base Power at count 0; boosters
    // transform each incoming packet at that packet's own chain position.
    // Rarity multiplies the primary effect (the Core is exempt; it grows by upgrades).
    let outBuckets: Buckets | undefined;
    if (def.source) {
      // The Core levels EXPONENTIALLY (the one intended long-run engine);
      // loot sources level with the standard diminishing scale.
      const lvlScale = isMechCore(node.defId) ? coreLevelMult(node.level) : upgradeScale(node.level);
      let baseOut = D(def.source.basePower).mul(lvlScale).mul(rarityMultOf(def, inst)).mul(D(1).add(affixSum(inst, 'power')));
      if (isMechCore(node.defId)) baseOut = mul(mul(baseOut, coreTierMult(tier)), D(coreBoost));
      outBuckets = new Map([[0, { v: baseOut, full: baseOut }]]);
    } else if (def.amplifier) {
      // The booster's bonus decays with chain position (the stacking penalty);
      // the multiplier itself never drops below ×1, so more is always more.
      const mult = D(def.amplifier.multBase + def.amplifier.multPerLevel * levelGrowth(node.level)).mul(rarityMultOf(def, inst)).mul(
        D(1).add(affixSum(inst, 'power')),
      );
      const inB = bucketsIn.get(node.id) ?? new Map<number, FlowBucket>();
      outBuckets = new Map();
      let inTotal = D(0);
      let effWeighted = D(0);
      let topPos = 0;
      for (const [count, pkt] of inB) {
        const pos = count + 1;
        const eff = boostEffectiveness(pos, conductance);
        const effMult = D(1).add(mult.sub(1).mul(eff));
        outBuckets.set(pos, { v: mul(pkt.v, effMult), full: mul(pkt.full, mult) });
        inTotal = add(inTotal, pkt.v);
        effWeighted = add(effWeighted, mul(pkt.v, D(eff)));
        if (pos > topPos) topPos = pos;
      }
      if (topPos > 0) {
        boosterInfo[node.id] = { eff: inTotal.gt(0) ? effWeighted.div(inTotal).toNumber() : 1, topPos };
      }
    }
    if (outBuckets === undefined) continue;
    let total = D(0);
    for (const pkt of outBuckets.values()) total = add(total, pkt.v);
    powerOut.set(node.id, total);

    // Distribute per OUTPUT PORT, applying the connection-splitting divisor:
    // every packet divides evenly among the wires it feeds, counts unchanged.
    // (Multiple flows into one input merge per count — the additive
    // "supercharge" at attack intakes is full value again.)
    const outPorts = portsForNode(node.defId, tier).filter((p) => p.direction === 'out');
    for (const op of outPorts) {
      const portEdges = graph.edges.filter((e) => e.fromNode === node.id && e.fromPort === op.id);
      if (portEdges.length === 0) continue;
      const n = D(portEdges.length);
      for (const e of portEdges) {
        const target = bucketsIn.get(e.toNode);
        let edgeV = D(0);
        let edgeFull = D(0);
        for (const [count, pkt] of outBuckets) {
          const shareV = div(pkt.v, n);
          const shareFull = div(pkt.full, n);
          edgeV = add(edgeV, shareV);
          edgeFull = add(edgeFull, shareFull);
          if (!target) continue;
          const cur = target.get(count);
          if (cur) {
            cur.v = add(cur.v, shareV);
            cur.full = add(cur.full, shareFull);
          } else {
            target.set(count, { v: shareV, full: shareFull });
          }
        }
        edgeFlow[e.id] = edgeV;
        edgeEff[e.id] = edgeFull.gt(0) ? edgeV.div(edgeFull).toNumber() : 1;
      }
    }
  }

  // summed power per node (the public readout shape stays scalar)
  const powerIn = new Map<string, Decimal>();
  for (const [id, b] of bucketsIn) {
    let total = D(0);
    for (const pkt of b.values()) total = add(total, pkt.v);
    powerIn.set(id, total);
  }

  // ---- PHASE 2: resolve attacks ----
  // No intake gate anymore: stacking penalties were already paid at each
  // booster along the way, so an attack consumes its summed flow at face value.

  const attacks: EffectiveAttack[] = [];
  const attackInfo: Record<string, AttackBreakdown> = {};
  const overloaded = false; // weight-overload handled by caller (combat) via canEquip; eval reports weight
  for (const node of graph.nodes) {
    const def = getPartDef(node.defId);
    if (!def.attack) continue;
    const inst = instOf(node.id);
    const scale = upgradeScale(node.level);
    const a = def.attack;

    // own affixes as a mod bundle, merged with wired tuner mods
    const mods = emptyMods();
    mods.critChanceAdd += affixSum(inst, 'critChance').toNumber();
    mods.critMultAdd = add(mods.critMultAdd, affixSum(inst, 'critMult'));
    mods.powerAdd = add(mods.powerAdd, affixSum(inst, 'power'));
    mods.cooldownRed += affixSum(inst, 'cooldown').toNumber();
    mergeMod(mods, modIn.get(node.id) ?? emptyMods());

    const flowIn = powerIn.get(node.id) ?? D(0);
    const base = D(a.baseDamage).mul(scale).mul(rarityMultOf(def, inst));
    const flowContribution = mul(flowIn, D(a.flowScale));
    const preMod = base.add(flowContribution);
    const damage = preMod.mul(D(1).add(mods.powerAdd)).mul(mods.powerMult);
    const critChance = clamp01(a.baseCritChance + mods.critChanceAdd);
    const critMult = add(D(a.baseCritMult), mods.critMultAdd);
    const cooldownMs = Math.max(MIN_COOLDOWN_MS, a.cooldownMs * (1 - Math.min(CD_RED_CAP, mods.cooldownRed)));

    attacks.push({
      sourceNodeId: node.id,
      defId: def.id,
      name: def.name,
      kind: a.kind,
      damageType: a.damageType,
      damage,
      cooldownMs,
      critChance,
      critMult,
    });
    attackInfo[node.id] = {
      base,
      flowIn,
      flowScale: D(a.flowScale),
      flowContribution,
      powerAddFrac: mods.powerAdd,
      powerMult: mods.powerMult,
      critChanceAdd: mods.critChanceAdd,
      critMultAdd: mods.critMultAdd,
      cooldownRed: mods.cooldownRed,
    };
  }

  global.overloaded = overloaded;
  const powerInByNode: Record<string, Decimal> = {};
  for (const [k, v] of powerIn) powerInByNode[k] = v;
  const powerOutByNode: Record<string, Decimal> = {};
  for (const [k, v] of powerOut) powerOutByNode[k] = v;

  return { attacks, global, powerInByNode, powerOutByNode, edgeFlow, edgeEff, boosterInfo, attackInfo, equippedWeight };
}

function tunerBundle(
  def: ReturnType<typeof getPartDef>,
  level: number,
  inst: PartInstance | undefined,
): ModBundle {
  const b = emptyMods();
  if (!def.tuner) return b;
  // perLevel growth is diminishing and is the ONLY level scaling (a former
  // double-scale with upgradeScale made tuners grow quadratically).
  const amt = (def.tuner.base + def.tuner.perLevel * levelGrowth(level)) * rarityMultOf(def, inst);
  switch (def.tuner.stat) {
    case 'critChance':
      b.critChanceAdd += amt + affixSum(inst, 'critChance').toNumber();
      break;
    case 'critMult':
      b.critMultAdd = add(b.critMultAdd, D(amt).add(affixSum(inst, 'critMult')));
      break;
    case 'power':
      b.powerAdd = add(b.powerAdd, D(amt).add(affixSum(inst, 'power')));
      break;
    case 'cooldown':
      b.cooldownRed += amt + affixSum(inst, 'cooldown').toNumber();
      break;
  }
  return b;
}
